import { hotjar } from 'react-hotjar';
import { posthog } from 'posthog-js';
import { getFreeTrialDetails, getProductEnablement } from '../services/postHogServices';
import type { UserInfo } from '../types/auth';
import type { ProductEnablementData } from '../types/posthogTypes';
import { codeVersion, isLoggedKey, platFormVersion } from './constants';
import { hasAnalyticsConsent, onConsentChange } from './cookieConsent';
import { formatDate } from './index';
import { logger } from './logger';

// ==================== Type Definitions ====================
interface IpResponse {
  ip: string;
}

interface UserProperties {
  username: string;
  userid: string;
  emailAddress: string;
  orgid: string;
  organisationname: string;
  orgtype: string;
  jobTitle: string;
  orgEntityName: string;
  ip_address: string;
  freeTrial: boolean;
  freeTrial_count: number;
  freeTrial_start: string;
  freeTrial_end: string;
  codeVersion: string;
  platformVersion: string;
}

interface UserInfoResult {
  distinctId: string;
  properties: UserProperties;
}

export type EventProperties = Record<
  string,
  string | number | boolean | null | undefined | Array<string>
>;

const POSTHOG_GROUP_TYPE_ORGANISATION = 'organisation';

const ORG_TYPE_LABELS: Readonly<Record<string, string>> = {
  '1': 'Administrator',
  '2': 'Publisher',
  '3': 'Reviewer',
  '4': 'Viewer',
};

const resolveOrgTypeLabel = (
  rawOrgType: number | string | null | undefined,
): string => {
  if (rawOrgType == null || rawOrgType === '') {
    return '';
  }
  return ORG_TYPE_LABELS[String(rawOrgType)] ?? `unknown_${String(rawOrgType)}`;
};

interface OrganisationGroupProperties {
  name: string;
  orgId: string;
  orgType: string;
  OrgTypeName: string;
  orgEntityName?: string;
}

// ==================== State Management ====================
interface AnalyticsState {
  isInitialized: boolean;
  isEnabled: boolean;
}

// ENV flags
const envPostHogEnabled = process.env.REACT_APP_POSTHOG_ENABLED === 'true';
const envHotjarEnabled = process.env.REACT_APP_HOTJAR_ENABLED === 'true';
const cookieConsentFeatureEnabled = process.env.REACT_APP_COOKIE_CONSENT_ENABLED === 'true';

// Cookie consent state
let cookieConsentEnabled = false;

const postHogState: AnalyticsState = {
  isInitialized: false,
  isEnabled: false, // Default disabled - requires both API flag AND cookie consent
};

const hotjarState: AnalyticsState = {
  isInitialized: false,
  isEnabled: false, // Default disabled - requires both API flag AND cookie consent
};

let cachedIpAddress: string | null = null;
let ipFetchPromise: Promise<string | null> | null = null;
let hotjarUserIdentified = false;
const FREE_TRIAL_CACHE_KEY = 'posthog_free_trial_cache';

type FreeTrialCache = {
  freeTrial: boolean;
  freeTrial_count: number;
  freeTrial_start: string;
  freeTrial_end: string;
};

let cachedFreeTrialData: FreeTrialCache | null = null;

const getPersistedFreeTrialData = (): FreeTrialCache | null => {
  try {
    const raw = sessionStorage.getItem(FREE_TRIAL_CACHE_KEY);
    return raw ? (JSON.parse(raw) as FreeTrialCache) : null;
  } catch {
    return null;
  }
};

const persistFreeTrialData = (data: FreeTrialCache): void => {
  try {
    sessionStorage.setItem(FREE_TRIAL_CACHE_KEY, JSON.stringify(data));
  } catch { /* quota errors are non-critical */ }
};

export const clearFreeTrialCache = (): void => {
  cachedFreeTrialData = null;
  sessionStorage.removeItem(FREE_TRIAL_CACHE_KEY);
};

const PRODUCT_ENABLEMENT_CACHE_KEY = 'posthog_product_enablement_cache';

let cachedProductEnablement: ProductEnablementData | null = null;
let productEnablementPromise: Promise<ProductEnablementData | null> | null = null;
let freeTrialPromise: Promise<{
  freeTrial: boolean;
  freeTrial_count: number;
  freeTrial_start: string;
  freeTrial_end: string;
} | null> | null = null;
let consentCleanup: (() => void) | null = null;

const isProductEnablementData = (value: unknown): value is ProductEnablementData => {
  if (value == null || typeof value !== 'object') {
    return false;
  }
  const data = value as Partial<ProductEnablementData>;
  return (
    typeof data.hasAiSearchAccess === 'boolean' &&
    typeof data.hasLeiAccess === 'boolean' &&
    Array.isArray(data.leiAccessProducts) &&
    typeof data.hasPdfViewerAccess === 'boolean' &&
    Array.isArray(data.pdfViewerAccessProducts) &&
    Array.isArray(data.productAccess)
  );
};

const getPersistedProductEnablement = (): ProductEnablementData | null => {
  try {
    const raw = localStorage.getItem(PRODUCT_ENABLEMENT_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isProductEnablementData(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const persistProductEnablement = (data: ProductEnablementData): void => {
  try {
    localStorage.setItem(PRODUCT_ENABLEMENT_CACHE_KEY, JSON.stringify(data));
  } catch { /* quota errors are non-critical */ }
};

const normalizeProductEnablement = (data: ProductEnablementData): ProductEnablementData => ({
  hasAiSearchAccess: data.hasAiSearchAccess,
  hasLeiAccess: data.hasLeiAccess,
  leiAccessProducts: data.leiAccessProducts ?? [],
  hasPdfViewerAccess: data.hasPdfViewerAccess,
  pdfViewerAccessProducts: data.pdfViewerAccessProducts ?? [],
  productAccess: data.productAccess ?? [],
});

export const clearProductEnablementCache = (): void => {
  cachedProductEnablement = null;
  productEnablementPromise = null;
  try {
    localStorage.removeItem(PRODUCT_ENABLEMENT_CACHE_KEY);
  } catch { /* ignore storage errors during logout */ }
};

// ==================== Enable/Disable Controls ====================
/**
* Update PostHog enabled state based on ENV flag and optional cookie consent
*/
const updatePostHogState = (): void => {
  const shouldBeEnabled = cookieConsentFeatureEnabled
    ? envPostHogEnabled && cookieConsentEnabled
    : envPostHogEnabled;

  if (postHogState.isEnabled !== shouldBeEnabled) {
    postHogState.isEnabled = shouldBeEnabled;
    logger.log(`PostHog: ${shouldBeEnabled ? 'Enabled' : 'Disabled'} (ENV: ${envPostHogEnabled}, Cookie: ${cookieConsentEnabled})`);

    // If disabled and initialized, we might want to opt out
    if (!shouldBeEnabled && postHogState.isInitialized && posthog !== undefined) {
      try {
        // PostHog doesn't have a direct opt-out, but we prevent future events
        logger.log('PostHog: Consent revoked, future events will be blocked');
      } catch (error) {
        logger.error('PostHog: Error handling consent revocation:', error);
      }
    }
  }
};

/**
* Update Hotjar enabled state based on ENV flag and optional cookie consent
*/
const updateHotjarState = (): void => {
  const shouldBeEnabled = cookieConsentFeatureEnabled
    ? envHotjarEnabled && cookieConsentEnabled
    : envHotjarEnabled;

  if (hotjarState.isEnabled !== shouldBeEnabled) {
    hotjarState.isEnabled = shouldBeEnabled;
    logger.log(`Hotjar: ${shouldBeEnabled ? 'Enabled' : 'Disabled'} (ENV: ${envHotjarEnabled}, Cookie: ${cookieConsentEnabled})`);

    // If disabled and initialized, we might want to opt out
    if (!shouldBeEnabled && hotjarState.isInitialized) {
      try {
        // Hotjar doesn't have a direct opt-out, but we prevent future events
        logger.log('Hotjar: Consent revoked, future events will be blocked');
      } catch (error) {
        logger.error('Hotjar: Error handling consent revocation:', error);
      }
    }
  }
};

/**
* Update cookie consent state
* Final enabled state = ENV flag AND cookie consent (when consent feature enabled)
*/
const updateCookieConsent = (hasConsent: boolean): void => {
  cookieConsentEnabled = hasConsent;
  updatePostHogState();
  updateHotjarState();
};

/**
* Stop cookie consent listener
*/
export const stopCookieConsentListener = (): void => {
  if (consentCleanup) {
    consentCleanup();
    consentCleanup = null;
    logger.log('Stopped cookie consent listener');
  }
};

/**
* Get current analytics states
*/
export const getAnalyticsState = (): { postHog: AnalyticsState; hotjar: AnalyticsState } => ({
  postHog: { ...postHogState },
  hotjar: { ...hotjarState },
});

// ==================== Helpers ====================
/**
* Get user data from localStorage
*/
export const getUserData = (): UserInfo | null => {
  try {
    const userDataString = localStorage.getItem('userData');
    if (!userDataString) {
      return null;
    }
    return JSON.parse(userDataString) as UserInfo;
  } catch {
    return null;
  }
};

/**
* Get client IP address (cached after first fetch)
*/
const getIpAddress = async (): Promise<string | null> => {
  if (cachedIpAddress) {
    return cachedIpAddress;
  }

  if (ipFetchPromise) {
    return ipFetchPromise;
  }

  ipFetchPromise = fetch('https://api.ipify.org?format=json')
    .then((response) => response.json() as Promise<IpResponse>)
    .then((data) => {
      cachedIpAddress = data.ip ?? null;
      return cachedIpAddress;
    })
    .catch(() => {
      return null;
    })
    .finally(() => {
      ipFetchPromise = null;
    });

  return ipFetchPromise;
};


/** True only when the `isLoggedIn` cookie is present (set on T&C accept). */
const hasIsLoggedInCookie = (): boolean => {
  if (typeof document === 'undefined') return false;
  const prefix = `${isLoggedKey}=`;
  return document.cookie.split('; ').some((entry) => entry.startsWith(prefix));
};

const getFreeTrialData = async () => {
  if (!hasIsLoggedInCookie()) {
    return null;
  }
  if (cachedFreeTrialData) {
    return cachedFreeTrialData;
  }
  const persisted = getPersistedFreeTrialData();
  if (persisted) {
    cachedFreeTrialData = persisted;
    return persisted;
  }
  if (freeTrialPromise) {
    return freeTrialPromise;
  }
  const userData = getUserData();
  if (!userData?.userId || !userData?.orgId) {
    return null;
  }
  freeTrialPromise = getFreeTrialDetails(userData.userId, userData.orgId)
    .then((data) => {
      if (!data) {
        return null;
      }
      const startDate = data.startDate;
      const endDate = data.endDate;
      const result: FreeTrialCache = {
        freeTrial: (data.freeTrialCount ?? 0) > 0,
        freeTrial_count: data.freeTrialCount ?? 0,
        freeTrial_start: startDate ? formatDate(startDate, 'YYYY-MM-DD') : '',
        freeTrial_end: endDate ? formatDate(endDate, 'YYYY-MM-DD') : '',
      };
      cachedFreeTrialData = result;
      persistFreeTrialData(result);
      return result;
    })
    .catch(() => null)
    .finally(() => {
      freeTrialPromise = null;
    });
  return freeTrialPromise;
};

const getProductEnablementData = async (): Promise<ProductEnablementData | null> => {
  if (!hasIsLoggedInCookie()) {
    return null;
  }
  if (cachedProductEnablement) {
    return cachedProductEnablement;
  }
  const persisted = getPersistedProductEnablement();
  if (persisted) {
    cachedProductEnablement = persisted;
    return persisted;
  }
  if (productEnablementPromise) {
    return productEnablementPromise;
  }
  productEnablementPromise = getProductEnablement()
    .then((data) => {
      if (!data) {
        return null;
      }
      const result = normalizeProductEnablement(data);
      cachedProductEnablement = result;
      persistProductEnablement(result);
      return result;
    })
    .catch(() => null)
    .finally(() => {
      productEnablementPromise = null;
    });
  return productEnablementPromise;
};

const toEnablementPersonProperties = (
  enablement: ProductEnablementData | null,
): ProductEnablementData => ({
  hasAiSearchAccess: enablement?.hasAiSearchAccess ?? false,
  hasLeiAccess: enablement?.hasLeiAccess ?? false,
  leiAccessProducts: enablement?.leiAccessProducts ?? [],
  hasPdfViewerAccess: enablement?.hasPdfViewerAccess ?? false,
  pdfViewerAccessProducts: enablement?.pdfViewerAccessProducts ?? [],
  productAccess: enablement?.productAccess ?? [],
});

/**
* Build user properties object (shared between PostHog and Hotjar)
*/
const buildUserProperties = async (): Promise<UserInfoResult | null> => {
  const userData = getUserData();
  if (!userData) {
    return null;
  }

  const firstName = userData.firstName ?? '';
  const lastName = userData.lastName ?? '';
  const username = `${firstName} ${lastName}`.trim();
  const userId = userData.userId;
  const distinctId = `${username}_${userId ?? ''}`;
  const ipAddress = await getIpAddress();
  const freeTrialData = await getFreeTrialData();

  const properties: UserProperties = {
    username: username,
    userid: userId?.toString() ?? '',
    emailAddress: userData.emailAddress ?? '',
    orgid: userData.orgId?.toString() ?? '',
    organisationname: userData.orgName ?? '',
    orgtype: userData.orgType?.toString() ?? '',
    jobTitle: userData.jobTitle ?? '',
    orgEntityName: userData.orgEntityName ?? '',
    ip_address: ipAddress ?? '',
    freeTrial: freeTrialData?.freeTrial ?? false,
    freeTrial_count: freeTrialData?.freeTrial_count ?? 0,
    freeTrial_start: freeTrialData?.freeTrial_start ?? '',
    freeTrial_end: freeTrialData?.freeTrial_end ?? '',
    codeVersion: codeVersion,
    platformVersion: platFormVersion,
  };

  return { distinctId, properties };
};

const buildOrganisationGroup = (
  userData: UserInfo,
): { groupKey: string; properties: OrganisationGroupProperties } | null => {
  if (userData.orgId == null) {
    return null;
  }

  const orgEntityName = userData.orgEntityName?.trim() ?? '';

  const properties: OrganisationGroupProperties = {
    name: userData.orgName ?? '',
    orgId: String(userData.orgId),
    orgType: userData.orgType?.toString() ?? '',
    OrgTypeName: resolveOrgTypeLabel(userData.orgType),
  };

  if (orgEntityName) {
    properties.orgEntityName = orgEntityName;
  }

  return { groupKey: String(userData.orgId), properties };
};

const identifyPostHogGroup = (): void => {
  const userData = getUserData();
  if (!userData) {
    return;
  }
  const group = buildOrganisationGroup(userData);
  if (!group) {
    return;
  }
  posthog.group(
    POSTHOG_GROUP_TYPE_ORGANISATION,
    group.groupKey,
    group.properties,
  );
  // Persist $groups as a super property so autocapture, $pageview, $pageleave,
  // and any other SDK-driven events also carry $group_0 until logout reset.
  posthog.register({
    $groups: { [POSTHOG_GROUP_TYPE_ORGANISATION]: group.groupKey },
  });
  logger.log('PostHog: Organisation group identified', group.groupKey);
};

// ==================== PostHog ====================
/**
* Identify user with PostHog
*/
const identifyPostHogUser = async (): Promise<void> => {
  if (!postHogState.isInitialized || !postHogState.isEnabled || posthog === undefined) {
    return;
  }

  try {
    const [userInfo, enablement] = await Promise.all([
      buildUserProperties(),
      getProductEnablementData(),
    ]);
    if (userInfo) {
      posthog.identify(userInfo.distinctId, {
        ...userInfo.properties,
        ...toEnablementPersonProperties(enablement),
      });
      identifyPostHogGroup();
    }
  } catch (error) {
    logger.error('PostHog: Failed to identify user', error);
  }
};

/**
* Initialize PostHog analytics
*/
export const initPostHog = (): void => {
  if (postHogState.isInitialized) {
    return;
  }

  const apiKey = process.env.REACT_APP_POSTHOG_API_KEY;
  const apiHost = process.env.REACT_APP_POSTHOG_API_HOST;

  if (!postHogState.isEnabled || !apiKey) {
    logger.log('PostHog: Not configured or disabled');
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: apiHost,
      person_profiles: 'identified_only',
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      loaded: () => {
        postHogState.isInitialized = true;
        void identifyPostHogUser();
      },
    });

    postHogState.isInitialized = true;

    // If userData is already in localStorage (e.g. authenticated user refreshing),
    // bind the organisation group synchronously right after init. This ensures
    // the very first auto-pageview and any autocapture events also carry $group_0,
    // not just custom events captured after the async identify flow completes.
    if (getUserData()) {
      try {
        identifyPostHogGroup();
      } catch (groupError) {
        logger.error('PostHog: Eager group bind failed', groupError);
      }
    }
  } catch (error) {
    logger.error('PostHog: Failed to initialize', error);
  }
};

// ==================== Hotjar ====================
/**
* Identify user with Hotjar (called ONCE per session)
* Hotjar identify should only be called once to set user attributes
*/
const identifyHotjarUser = async (): Promise<void> => {
  if (!hotjarState.isInitialized || !hotjarState.isEnabled || hotjarUserIdentified) {
    return;
  }

  try {
    const userInfo = await buildUserProperties();
    // Only set the one-shot flag AFTER a real identify happens. If userData
    // isn't ready yet (e.g. pre-login init), allow a retry when the user
    // eventually authenticates.
    if (!userInfo || hotjarUserIdentified) {
      return;
    }
    hotjarUserIdentified = true;
    hotjar.identify(userInfo.distinctId, userInfo.properties);
    logger.log('Hotjar: User identified', userInfo.distinctId);
  } catch (error) {
    logger.error('Hotjar: Failed to identify user', error);
  }
};

/**
 * AuthContext / preflight hook: on logout, reset Hotjar + analytics caches.
 * On login, re-run identify (free-trial / enablement inside identify still
 * depend only on `isLoggedKey` — no extra flags).
 */
export const setAuthStateForAnalytics = (isAuthenticated: boolean): void => {
  if (!isAuthenticated) {
    hotjarUserIdentified = false;
    freeTrialPromise = null;
    productEnablementPromise = null;
    clearFreeTrialCache();
    clearProductEnablementCache();
    if (postHogState.isInitialized && posthog !== undefined) {
      try {
        posthog.unregister('$groups');
        posthog.reset();
        logger.log('PostHog: Session reset on logout');
      } catch (error) {
        logger.error('PostHog: Failed to reset session on logout', error);
      }
    }
    return;
  }
  void identifyPostHogUser();
  void identifyHotjarUser();
};

/**
* Initialize Hotjar analytics
*/
export const initHotjar = (): void => {
  if (hotjarState.isInitialized) {
    return;
  }

  const hotjarId = process.env.REACT_APP_HOTJAR_ID;

  if (!hotjarState.isEnabled || !hotjarId) {
    logger.log('Hotjar: Not configured or disabled');
    return;
  }

  try {
    hotjar.initialize({ id: Number(hotjarId), sv: 6 });
    hotjarState.isInitialized = true;
    logger.log('Hotjar: Initialized successfully');
    void identifyHotjarUser();
  } catch (error) {
    logger.error('Hotjar: Failed to initialize', error);
  }
};

// ==================== Unified Analytics ====================
/**
* Initialize cookie consent listener
* This listens for changes in cookie consent and updates analytics state
*/

const initCookieConsentListener = (): void => {
  if (!cookieConsentFeatureEnabled) {
    return;
  }

  // Clean up existing listener if any
  if (consentCleanup) {
    consentCleanup();
  }

  // Check initial consent state
  const initialConsent = hasAnalyticsConsent();
  updateCookieConsent(initialConsent);

  // Listen for consent changes
  consentCleanup = onConsentChange((hasAnalytics) => {
    updateCookieConsent(hasAnalytics);

    // If consent is granted and analytics weren't initialized, try to initialize now
    if (hasAnalytics && !postHogState.isInitialized && envPostHogEnabled) {
      initPostHog();
    }
    if (hasAnalytics && !hotjarState.isInitialized && envHotjarEnabled) {
      initHotjar();
    }
  });

  logger.log('Cookie consent listener initialized');
};

/**
* Initialize all analytics platforms
* Fetches enable/disable flags from API before initialization
* Checks cookie consent before enabling
* Starts periodic flag checking every 5 minutes
*/
export const initAnalytics = (): Promise<void> => {
  if (cookieConsentFeatureEnabled) {
    initCookieConsentListener();
    const hasConsent = hasAnalyticsConsent();
    updateCookieConsent(hasConsent);
  } else {
    cookieConsentEnabled = true;
    updatePostHogState();
    updateHotjarState();
  }

  if (postHogState.isEnabled) {
    initPostHog();
  } else {
    logger.log('PostHog: Not initialized - waiting for ENV flag and/or cookie consent');
  }

  if (hotjarState.isEnabled) {
    initHotjar();
  } else {
    logger.log('Hotjar: Not initialized - waiting for ENV flag and/or cookie consent');
  }
  return Promise.resolve();
};

/**
* Capture event without user identity (for pre-login pages e.g. Login).
* Does not call identify or attach user/freeTrial; same ENV and consent rules apply.
*/
export const captureEventAnonymous = (
  eventName: string,
  properties?: EventProperties,
): void => {
  const eventProperties: EventProperties = {
    NextGen: true,
    customEvent: true,
    codeVersion: codeVersion,
    platformVersion: platFormVersion,
    ...properties,
  };
  if (postHogState.isInitialized && postHogState.isEnabled && posthog !== undefined) {
    try {
      posthog.capture(eventName, eventProperties);
      logger.log('PostHog event captured (anonymous):', eventName, eventProperties);
    } catch (error) {
      logger.error(`PostHog: Failed to capture anonymous event "${eventName}":`, error);
    }
  }
  if (hotjarState.isInitialized && hotjarState.isEnabled) {
    try {
      hotjar.event(eventName);
      logger.log('Hotjar event captured (anonymous):', eventName);
    } catch (error) {
      logger.error(`Hotjar: Failed to capture anonymous event "${eventName}":`, error);
    }
  }
};

/**
* Capture event on all enabled analytics platforms
*/
export const captureEventInternal = async (
  eventName: string,
  properties?: EventProperties,
): Promise<void> => {
  const eventProperties: EventProperties = {
    NextGen: true,
    customEvent: true,
    codeVersion: codeVersion,
    platformVersion: platFormVersion,
    ...properties,
  };
  // PostHog capture - supports event name + properties
  if (postHogState.isInitialized && postHogState.isEnabled && posthog !== undefined) {
    try {
      await identifyPostHogUser();
      posthog.capture(eventName, eventProperties);
      logger.log('PostHog event captured:', eventName, eventProperties);
    } catch (error) {
      logger.error(`PostHog: Failed to capture event "${eventName}":`, error);
    }
  }

  // Hotjar capture - NOTE: Hotjar events only support event name, NOT properties
  if (hotjarState.isInitialized && hotjarState.isEnabled) {
    try {
      await identifyHotjarUser();
      hotjar.event(eventName);
      logger.log('Hotjar event captured:', eventName);
    } catch (error) {
      logger.error(`Hotjar: Failed to capture event "${eventName}":`, error);
    }
  }
};
 